import { CUSTOMER_RFQ_PROTOCOL_RULES_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useCounterpartiesFilter } from '@src/hooks/filters/useCounterpartyFilter';
import { useCustomers } from '@src/hooks/useCustomer';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import {
  ACTION,
  DEFAULT_BULK_EDIT_MAX_ROWS,
  DEFAULT_PAGINATION_SIZE,
  EntityAdmin,
  ModeEnum,
  RFQProtocolEnum,
  useSecuritiesContext,
  useSymbolsFilter,
  type ICustomerOrderExecutionRule,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { DEFAULT_CUSTOMER_CREDITS_CURRENCIES } from '../CustomerCredits/utils';
import {
  RFQ_PROTOCOL_RULES_BULK_EDIT_FIELDS,
  RFQ_PROTOCOL_RULES_COLUMNS,
  getRFQProtocolRulesDrawerOptions,
} from './utils';

export function CustomerRFQProtocolRules() {
  const { isAuthorized } = useRoleAuth();
  const { getConfig } = useOrgConfiguration();
  const { allowSyntheticRFQ } = useFeatureFlag();

  const customers = useCustomers();
  const { securitiesBySymbol } = useSecuritiesContext();

  const counterpartiesFilter = useCounterpartiesFilter();
  const symbolsFilter = useSymbolsFilter();
  const creditCurrencies = useMemo(() => {
    const additionalCreditCurrencies = getConfig(OrgConfigurationKey.AdditionalCustomerCreditCurrencies, '')
      .split(',')
      .map(currency => currency.trim())
      .filter(currency => !!currency);

    const allCurrencies = [...DEFAULT_CUSTOMER_CREDITS_CURRENCIES];
    for (const currency of additionalCreditCurrencies) {
      allCurrencies.push({ Symbol: currency, Description: '' });
    }
    return allCurrencies;
  }, [getConfig]);

  const availableFilterableProperties = useMemo(() => {
    return [counterpartiesFilter, symbolsFilter];
  }, [counterpartiesFilter, symbolsFilter]);
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_RFQ_PROTOCOL_RULES_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );

  const drawerOptions = useMemo(() => {
    if (!customers) {
      return undefined;
    }
    return getRFQProtocolRulesDrawerOptions({
      customers,
      securitiesBySymbol,
      creditCurrencies,
      allowSyntheticRFQ,
    });
  }, [allowSyntheticRFQ, creditCurrencies, customers, securitiesBySymbol]);

  const canEdit = isAuthorized(ACTION.DEALER_TRADING);

  return (
    <EntityAdmin<ICustomerOrderExecutionRule>
      title="RFQ Protocol Rules"
      subtitle="Controls whether Customer RFQs are priced automatically (Systematic) or manually (Manual). Rules are evaluated by Priority."
      entityName="RFQ Protocol Rule"
      path="/customer/rfq-protocol-rules"
      entityIDField="RuleID"
      columns={RFQ_PROTOCOL_RULES_COLUMNS}
      drawerOptions={drawerOptions}
      filterableProperties={filterableProperties}
      externallyControlledFilter={externallyControlledFilter}
      allowAddEntity={canEdit}
      allowEditEntity={canEdit}
      allowDeleteEntity={canEdit}
      allowModeSwitch={{ isDisabled: () => !canEdit }}
      newEntityDefault={() => ({
        Mode: ModeEnum.Enabled,
        RFQProtocol: RFQProtocolEnum.Systematic,
        Priority: 0,
      })}
      persistKey="dealer/rfq-protocol-rules"
      allowBulkEdit={canEdit}
      bulkEditFields={RFQ_PROTOCOL_RULES_BULK_EDIT_FIELDS}
      maxBulkEditRows={getConfig(OrgConfigurationKey.EntityAdminMaxBulkEditRows, DEFAULT_BULK_EDIT_MAX_ROWS)}
      paginationSize={DEFAULT_PAGINATION_SIZE}
    />
  );
}
